import { useEffect, useMemo, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIntl } from '@atlassian/jira-intl';
import {
	type MenuItem,
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import {
	BOTTOM_OF_LIST,
	BACKLOG,
	BOARD,
	TOP_OF_LIST,
	ISSUE_LIST_BACKLOG_STRATEGY,
	KANBAN_BACKLOG_STRATEGY,
} from '../../../../../../model/constants.tsx';
import type { CardList } from '../../../../../../model/types.tsx';
import { cardsMove } from '../../../../../../state/actions/cards/move/index.tsx';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../../../state/index.tsx';
import { getBacklogStrategy } from '../../../../../../state/selectors/app/index.tsx';
import {
	getCardLists,
	makeGetCardListIdByCardId,
	makeIsCardOnBoard,
} from '../../../../../../state/selectors/cards/index.tsx';
import { canEditIssue } from '../../../../../../state/selectors/permissions/index.tsx';
import {
	getAllSprints,
	makeGetOtherOrderedSprints,
	makeIsCardListActiveSprint,
} from '../../../../../../state/selectors/sprints/index.tsx';
import { useIsCMPBacklog } from '../../../../../../state/state-hooks/capabilities.tsx';
import {
	MOVE_TO_MENU_ID,
	MOVE_TO_TOP_OF_BACKLOG_MENU_ID,
	MOVE_TO_BOTTOM_OF_BACKLOG_MENU_ID,
	MOVE_TO_BOARD_MENU_ID,
	MOVE_TO_SEARCH_MENU_ID,
	SEPARATOR_MENU_ID,
} from '../../constants.tsx';
import messages from '../../messages.tsx';
import { useRankIssueItems } from '../rank-issue/index.tsx';
import type { ContentProps, Props } from './types.tsx';

export const SEARCH_THRESHOLD = 5;

export const useMoveToContent = ({
	sprints,
	filteredSprints,
	onMoveToSprint,
	onMoveToBacklog,
	onMoveToBoard,
	canMoveIssue,
	isMoveToBoardEnabled,
	setQuery,
	isSearching,
	numOfSelectedSprints,
	sprintId,
	issueId,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	const sprintOptions: MenuItem[] = filteredSprints.map((sprint) => ({
		type: MenuNodeType.Item,
		id: `actions/move-to/${sprint.id}`,
		label: sprint.name,
		analytics: {
			...(isSearching && { actionSubjectId: 'moveToSprintSearchDropdownItem' }),
			attributes: {
				numOfSelectedSprints,
				sprintId: sprintId ?? 'backlog',
			},
		},
		onClick: (analyticsEvent) => onMoveToSprint(sprint.id, analyticsEvent),
	}));

	const sprintsSearchMenu: MenuNode[] = [
		{
			type: MenuNodeType.Search,
			id: MOVE_TO_SEARCH_MENU_ID,
			children: sprintOptions,
			hasSearch: sprints.length > SEARCH_THRESHOLD,
			onChange: (value) => setQuery(value),
			isAsyncSearch: false,
			ariaLabel: formatMessage(messages.sprintSearchPlaceholder),
		},
	];

	const boardLabel = useBacklogSelector(getCardLists)[BOARD]?.name;
	const isCMPBoard = useIsCMPBacklog();

	const moveToBoard: MenuNode[] = isMoveToBoardEnabled
		? [
				{
					type: MenuNodeType.Item,
					id: MOVE_TO_BOARD_MENU_ID,
					label: isCMPBoard && boardLabel ? boardLabel : formatMessage(messages.board),
					analytics: {
						attributes: {
							numOfSelectedSprints,
							sprintId: sprintId ?? 'backlog',
						},
					},
					onClick: onMoveToBoard,
				},
			]
		: [];

	const menuWithSeparator: MenuNode[] =
		sprints.length || moveToBoard.length
			? [
					...(moveToBoard.length ? moveToBoard : sprintsSearchMenu),
					{
						id: SEPARATOR_MENU_ID,
						type: MenuNodeType.Separator,
					},
				]
			: [];

	const rankIssueItems = useRankIssueItems({ issueId });

	return canMoveIssue
		? [
				{
					type: MenuNodeType.Item,
					label: formatMessage(messages.moveTo),
					id: MOVE_TO_MENU_ID,
					children: [
						...menuWithSeparator,
						{
							type: MenuNodeType.Item,
							id: MOVE_TO_TOP_OF_BACKLOG_MENU_ID,
							label: formatMessage(messages.topOfTheBacklog),
							analytics: {
								attributes: {
									numOfSelectedSprints,
									sprintId: sprintId ?? 'backlog',
								},
							},
							onClick: (analyticsEvent) => onMoveToBacklog(TOP_OF_LIST, analyticsEvent),
						},
						...rankIssueItems,
						{
							type: MenuNodeType.Item,
							id: MOVE_TO_BOTTOM_OF_BACKLOG_MENU_ID,
							label: formatMessage(messages.bottomOfTheBacklog),
							analytics: {
								attributes: {
									numOfSelectedSprints,
									sprintId: sprintId ?? 'backlog',
								},
							},
							onClick: (analyticsEvent) => onMoveToBacklog(BOTTOM_OF_LIST, analyticsEvent),
						},
					],
				},
			]
		: [];
};

export const useMoveTo = ({ issueId, cardIds, hide = false }: Props) => {
	const [query, setQuery] = useState('');
	const [filteredSprints, setFilteredSprints] = useState<CardList[]>([]);

	const backlogStrategy = useBacklogSelector(getBacklogStrategy);

	const getCardListIdByCardId = useBacklogSelector(makeGetCardListIdByCardId);

	const originCardListId = useMemo(
		() => getCardListIdByCardId(issueId),
		[getCardListIdByCardId, issueId],
	);

	const numOfSelectedSprints = useMemo(
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		() => new Set(cardIds.map(getCardListIdByCardId!)).size,
		[cardIds, getCardListIdByCardId],
	);

	const isFromActiveSprint = useBacklogSelector((state) =>
		makeIsCardListActiveSprint(state)(originCardListId || ''),
	);

	const showAllSprints = !!numOfSelectedSprints && numOfSelectedSprints > 1;

	const sprints = useBacklogSelector(
		(state) =>
			showAllSprints
				? getAllSprints(state)
				: makeGetOtherOrderedSprints(state)(originCardListId || ''),
		(before, after) =>
			before.length === after.length &&
			before.every((sprint, index) => sprint.name === after[index].name),
	);

	const isMoveToBoardEnabled = useBacklogSelector(
		(state) =>
			!makeIsCardOnBoard(state)(issueId) &&
			(backlogStrategy === ISSUE_LIST_BACKLOG_STRATEGY ||
				backlogStrategy === KANBAN_BACKLOG_STRATEGY) &&
			sprints.length === 0,
	);

	useEffect(() => {
		setFilteredSprints(
			sprints.filter((sprint) => sprint.name.toLowerCase().includes(query.toLowerCase())),
		);
	}, [query, sprints]);

	const onMoveToSprint = useBacklogActionCreator((id: string, analyticsEvent: UIAnalyticsEvent) =>
		cardsMove(
			{
				cardListId: id,
				placement: BOTTOM_OF_LIST,
				isBetweenLists: originCardListId !== id,
				isFromActiveSprint,
				cardIds,
			},
			analyticsEvent,
		),
	);

	const onMoveToBacklog = useBacklogActionCreator(
		(placement: typeof TOP_OF_LIST | typeof BOTTOM_OF_LIST, analyticsEvent: UIAnalyticsEvent) =>
			cardsMove(
				{
					cardListId: BACKLOG,
					placement,
					isBetweenLists: originCardListId !== BACKLOG,
					isFromActiveSprint,
					cardIds,
				},
				analyticsEvent,
			),
	);

	const onMoveToBoard = useBacklogActionCreator((analyticsEvent: UIAnalyticsEvent) =>
		cardsMove(
			{
				cardListId: BOARD,
				placement: BOTTOM_OF_LIST,
				isBetweenLists: originCardListId !== BOARD,
				isFromActiveSprint: false,
				cardIds,
			},
			analyticsEvent,
		),
	);

	const canEdit = useBacklogSelector(canEditIssue);
	const canMoveIssue = canEdit && !hide;

	return useMoveToContent({
		sprints,
		issueId,
		filteredSprints,
		isMoveToBoardEnabled,
		onMoveToSprint,
		onMoveToBacklog,
		onMoveToBoard,
		setQuery,
		canMoveIssue,
		isSearching: !!query,
		numOfSelectedSprints,
		sprintId: originCardListId || '',
	});
};
